import { useRef, useState, useEffect, useMemo } from "react";

const HorizontalWaveform = ({
  duration, rotation, onSeek, color = "purple",
  inMarker, outMarker, showMarkers = false
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [localProgress, setLocalProgress] = useState(0);
  const lastUpdate = useRef(0);

  useEffect(() => {
    if (!isDragging && duration > 0) {
      setLocalProgress((Number(rotation) || 0) / 360);
    }
  }, [rotation, duration, isDragging]);

  const colors = useMemo(() => {
    const isP = color === 'purple';
    return {
      stroke: isP ? '#A855F7' : '#22D3EE', fill: isP ? 'rgba(112, 0, 255, 0.1)' : 'rgba(0, 255, 127, 0.05)', 
      fillPlayed: isP ? 'rgba(168, 85, 247, 0.4)' : 'rgba(34, 211, 238, 0.4)', glow: isP ? '#7000FF' : '#39FF14', 
      playedStroke: isP ? '#E9D5FF' : '#A7F3D0', markerIn: '#FF4E4E', markerOut: '#00F2FF', 
      loopRegion: isP ? 'rgba(168, 85, 247, 0.2)' : 'rgba(34, 211, 238, 0.2)'
    };
  }, [color]);

  const waveformPoints = useMemo(() => {
    const pts = []; const num = 300;
    const seed = (color === 'purple' ? 1 : 2) * 123.45;
    for (let i = 0; i < num; i++) {
      const x = i / (num - 1);
      const amp = 0.35 + (Math.sin(x * 15 + seed) * 0.25) + (Math.sin(x * 40 - seed) * 0.15) + (Math.sin(x*100)*0.03);
      pts.push({ x, y: Math.max(0.1, Math.min(0.95, amp)) });
    }
    return pts;
  }, [color]);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDimensions({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
      }
    };
    update();
    window.addEventListener('resize', update);
  return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const { width: sw, height: sh } = dimensions;
    canvas.width = sw * dpr; canvas.height = sh * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, sw, sh);

    const cy = sh / 2; const maxA = sh * 0.45; const ppx = 80; 
    const vw = (duration > 0 ? duration : 300) * ppx;
    const phX = sw / 2; const vphX = (Number(localProgress) || 0) * vw;
    const shift = isNaN(phX - vphX) ? 0 : (phX - vphX);

    ctx.save(); ctx.translate(shift, 0);
    if (showMarkers && inMarker !== undefined && outMarker !== undefined) {
      ctx.fillStyle = colors.loopRegion; ctx.fillRect(inMarker * vw, 0, (outMarker - inMarker) * vw, sh);
    }
    ctx.beginPath(); ctx.moveTo(0, cy);
    waveformPoints.forEach(p => ctx.lineTo(p.x * vw, cy - p.y * maxA));
    for (let i = waveformPoints.length - 1; i >= 0; i--) {
        ctx.lineTo(waveformPoints[i].x * vw, cy + waveformPoints[i].y * maxA);
    }
    ctx.closePath();
    ctx.fillStyle = colors.fill; ctx.fill();
    ctx.strokeStyle = colors.stroke; ctx.lineWidth = 2; ctx.stroke();

    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, vphX, sh); ctx.clip();
    ctx.beginPath(); ctx.moveTo(0, cy);
    waveformPoints.forEach(p => ctx.lineTo(p.x * vw, cy - p.y * maxA));
    for (let i = waveformPoints.length - 1; i >= 0; i--) {
        ctx.lineTo(waveformPoints[i].x * vw, cy + waveformPoints[i].y * maxA);
    }
    ctx.closePath();
    ctx.fillStyle = colors.fillPlayed; ctx.fill();
    ctx.strokeStyle = colors.playedStroke; ctx.lineWidth = 3; 
    ctx.shadowBlur = 15; ctx.shadowColor = colors.glow; ctx.stroke();
    ctx.restore(); 

    if (showMarkers) {
      const m = (pos, col, lab, rig) => {
        const x = pos * vw;
        ctx.shadowBlur = 15; ctx.shadowColor = col; ctx.strokeStyle = col; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, sh); ctx.stroke();
        ctx.fillStyle = 'white'; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(lab, rig ? x + 8 : x - 28, 20); ctx.shadowBlur = 0;
      };
      m(inMarker, colors.markerIn, 'IN', true); m(outMarker, colors.markerOut, 'OUT', false);
    }
    ctx.restore(); 
    ctx.strokeStyle = 'white'; ctx.lineWidth = 3; ctx.shadowBlur = 15; ctx.shadowColor = 'white';
    ctx.beginPath(); ctx.moveTo(phX, 0); ctx.lineTo(phX, sh); ctx.stroke();
  }, [localProgress, dimensions, colors, duration, showMarkers, inMarker, outMarker, waveformPoints]);

  const handlePointerMove = (e) => {
    if (!isDragging || !duration) return;
    const ppx = 80; const vw = duration * ppx;
    const dx = -e.movementX * 15; 
    const next = Math.max(0, Math.min(1, localProgress + (dx / vw)));
    
    setLocalProgress(next);
    
    // THROTTLE: Invia il comando audio solo ogni 50ms per non bloccare il browser
    const now = Date.now();
    if (now - lastUpdate.current > 50) {
      if (onSeek) onSeek(next * 360);
      lastUpdate.current = now;
    }
  };

  return (
    <div ref={containerRef} className="waveform-container" 
      style={{ position:'relative', width:'100%', height:'100px', cursor:'grab', overflow:'hidden', background:'#050505', touchAction: 'none', border: '1px solid #222', borderRadius: '8px' }}
      onPointerDown={(e) => { setIsDragging(true); e.currentTarget.setPointerCapture(e.pointerId); }}
      onPointerMove={handlePointerMove}
      onPointerUp={(e) => { setIsDragging(false); e.currentTarget.releasePointerCapture(e.pointerId); if (onSeek) onSeek(localProgress * 360); }}
    >
      <canvas ref={canvasRef} style={{width:'100%', height:'100%', pointerEvents:'none'}} />
    </div>
  );
};
export default HorizontalWaveform;
